import React from 'react';
import {
  Mail,
  Phone,
  MapPin,
  Smartphone,
  Heart,
  Shield,
  Lock,
  FileText,
} from 'lucide-react';
import { ReactComponent as Logo } from '../assets/logo.svg';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    app: [
      {
        name: 'Baixar para Android',
        href: 'https://play.google.com/store/apps/details?id=br.android.finziin',
      },
      { name: 'O que você ganha', href: '#funcionalidades' },
      { name: 'Simples assim', href: '#como-funciona' },
      { name: 'Use sem pagar nada', href: '#gratuito' },
    ],
    company: [
      { name: 'Tire suas dúvidas', href: '#faq' },
      { name: 'Suporte', href: 'mailto:finziin.app@gmail.com' },
    ],
    legal: [
      { name: 'Política de Privacidade', href: '#privacidade' },
      { name: 'Termos de Uso', href: '#termos-uso' },
      { name: 'Segurança de Dados', href: '#seguranca' },
    ],
  };

  return (
    <footer className='bg-gray-900 text-white pt-16 pb-8'>
      <div className='container mx-auto px-4'>
        {/* Main Footer Grid */}
        <div className='grid lg:grid-cols-4 md:grid-cols-2 gap-8 mb-8'>
          {/* Logo & Description */}
          <div className='lg:col-span-1'>
            <a className='flex items-center space-x-2 mb-8'>
              <Logo className='w-10 h-10' />
              <span className='font-bold text-2xl transition-all'>
                <span className='text-blue-500'>Fin</span>
                <span className='text-emerald-500'>Ziin</span>
              </span>
            </a>
            <p className='text-gray-300 mb-6 leading-relaxed'>
              O FinZiin é um aplicativo de controle financeiro 100% gratuito
              para sempre, que ajuda você a organizar suas finanças de forma
              simples e segura.
            </p>
          </div>

          {/* App Links */}
          <div>
            <h3 className='text-lg font-semibold mb-6 text-white'>App</h3>
            <ul className='space-y-3'>
              {footerLinks.app.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    {...(link.name === 'Baixar para Android'
                      ? { target: '_blank', rel: 'noopener noreferrer' }
                      : {})}
                    className='text-gray-300 hover:text-blue-400 transition-colors duration-300 flex items-center space-x-2'
                  >
                    {link.name === 'Baixar para Android' && (
                      <Smartphone className='w-4 h-4' />
                    )}
                    <span>{link.name}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h3 className='text-lg font-semibold mb-6 text-white'>Empresa</h3>
            <ul className='space-y-3'>
              {footerLinks.company.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className='text-gray-300 hover:text-emerald-400 transition-colors duration-300 flex items-center space-x-2'
                  >
                    <span>{link.name}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className='text-lg font-semibold mb-6 text-white'>Legal</h3>
            <ul className='space-y-3'>
              {footerLinks.legal.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className='text-gray-300 hover:text-purple-400 transition-colors duration-300 flex items-center space-x-2'
                  >
                    {link.name === 'Política de Privacidade' && (
                      <FileText className='w-4 h-4' />
                    )}
                    {link.name === 'Termos de Uso' && (
                      <FileText className='w-4 h-4' />
                    )}
                    {link.name === 'Segurança de Dados' && (
                      <Shield className='w-4 h-4' />
                    )}
                    <span>{link.name}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Contact Information */}
        <div className='border-t border-gray-800 pt-8 mb-8'>
          <div className='grid md:grid-cols-3 gap-6'>
            <div className='flex items-center space-x-3'>
              <div className='bg-blue-500 p-2 rounded-lg'>
                <Mail className='w-5 h-5' />
              </div>
              <div>
                <div className='text-sm text-gray-400'>Email</div>
                <div className='text-white'>finziin.app@gmail.com</div>
              </div>
            </div>

            <div className='flex items-center space-x-3'>
              <div className='bg-emerald-500 p-2 rounded-lg'>
                <Phone className='w-5 h-5' />
              </div>
              <div>
                <div className='text-sm text-gray-400'>Suporte</div>
                <div className='text-white'>Direto no App</div>
              </div>
            </div>

            <div className='flex items-center space-x-3'>
              <div className='bg-purple-500 p-2 rounded-lg'>
                <MapPin className='w-5 h-5' />
              </div>
              <div>
                <div className='text-sm text-gray-400'>Localização</div>
                <div className='text-white'>São Paulo, Brasil</div>
              </div>
            </div>
          </div>
        </div>

        {/* Trust Badges */}
        <div className='border-t border-gray-800 pt-8 mb-8'>
          <div className='flex flex-wrap justify-center items-center gap-6 text-sm text-gray-400'>
            <div className='flex items-center space-x-2'>
              <Shield className='w-4 h-4 text-green-400' />
              <span>Dados Criptografados</span>
            </div>
            <div className='flex items-center space-x-2'>
              <Lock className='w-4 h-4 text-blue-400' />
              <span>LGPD Compliant</span>
            </div>
            <div className='flex items-center space-x-2'>
              <FileText className='w-4 h-4 text-purple-400' />
              <span>Código Auditado</span>
            </div>
            <div className='flex items-center space-x-2'>
              <Heart className='w-4 h-4 text-red-400' />
              <span>100% Gratuito para sempre</span>
            </div>
          </div>
        </div>

        {/* Download Section */}
        <div className='border-t border-gray-800 pt-8 mb-8'>
          <div className='text-center'>
            <h3 className='text-xl font-bold mb-4 text-white'>
              Baixe o FinZiin agora mesmo
            </h3>
            <p className='text-gray-300 mb-6'>
              Junte-se à nossa comunidade de usuários que já usam o FinZiin para
              organizar suas finanças.
            </p>
            <a
              href='https://play.google.com/store/apps/details?id=br.android.finziin'
              target='_blank'
              rel='noopener noreferrer'
              className='inline-flex items-center bg-gradient-to-r from-blue-500 to-emerald-500 hover:from-blue-600 hover:to-emerald-600 text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105'
            >
              <Smartphone className='w-5 h-5 mr-3' />
              Baixar na Google Play
            </a>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className='border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0'>
          <div className='text-gray-400 text-sm'>
            © {currentYear} FinZiin. Todos os direitos reservados.
          </div>

          <div className='flex items-center space-x-2 text-gray-400 text-sm'>
            <span>Feito com</span>
            <Heart className='w-4 h-4 text-red-400 fill-current' />
            <span>no Brasil</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
